/* The strings the whole public site shares.

   A kicker such as "Frequently asked questions", a crumb such as "Home" and a
   plan-table column such as "Price" are the same words on every page that
   renders them, but each page declared its own copy in its own `useI18n`
   dictionary. Twenty-three copies of four kickers in all: the English matched,
   and the two other languages did not.

   Chinese for "Before you decide" was `决定之前` on two pages and `做决定之前`
   on four, "Paying less does not mean getting less." was two different
   sentences, and the Malay for the RM299 build step read as two different
   phrases. Nobody edited a shared file, so every copy was free to drift, and it
   did. These strings now live here, once per language, and the pages read them
   from the root composer (a local `useI18n` scope falls back to the root for
   any key it does not declare itself).

   Only add a key here when the words belong to shared chrome or to a shared
   band rather than to one page's own copy. A string one page owns stays in that
   page's dictionary, where it cannot look shared by accident. The legal page
   labels are here for that reason: the footer nav and the two documents name the
   same three words, and the footer had already translated `Legal` one way while
   the Terms page translated it another. The two revision dates are not here,
   because they stamp two documents that are revised independently: a shared key
   would make one of them claim a revision that did not happen.

   The three labelled groups at the end of each language are the widest sweep of
   this pass. The home page pricing table and the web design plan table print the
   same twelve labels and the same twelve notes, both promo landing pages ask the
   buyer for the same six things, and the footer, header and breadcrumbs name the
   same places. Thirty-eight of those strings had drifted into two wordings, so
   the value kept is the one the page that owns the vocabulary already uses: the
   service page for the plan copy, the promo brief for the checklist. */

export const commonMessages = {
  'en-MY': {
    Home: 'Home',
    Blog: 'Blog',
    Portfolios: 'Portfolios',
    Promotions: 'Promotions',
    Services: 'Services',
    'Get in touch': 'Get in touch',
    /* The mobile menu button holds only its icon, so this is its accessible
       name. */
    Menu: 'Menu',
    'Web design': 'Web design',
    'Digital marketing': 'Digital marketing',
    'Brand presence': 'Brand presence',
    'Mobile application': 'Mobile application',
    'AI Solutions': 'AI Solutions',
    Days: 'Days',
    Hours: 'Hours',
    Minutes: 'Minutes',
    Seconds: 'Seconds',
    Legal: 'Legal',
    'Privacy Policy': 'Privacy Policy',
    'Terms of Use': 'Terms of Use',
    'Most Popular': 'Most Popular',
    Price: 'Price',
    Saves: 'Saves',
    'Paying less does not mean getting less.': 'Paying less does not mean getting less.',
    /* The project-type vocabulary. The home hero rotates the same words it
       prints as card tags, and the mobile page prints them again, so the seven
       words live here rather than once per page. */
    Business: 'Business',
    Catalog: 'Catalog',
    'E-Commerce': 'E-Commerce',
    Educational: 'Educational',
    Informational: 'Informational',
    Portal: 'Portal',
    'Visit Website': 'Visit Website',
    /* The Annual Pack's one-line summary. The promo index card and the landing
       page's meta description are the same sentence, so it is translated once
       here rather than twice. */
    'Pay your web design plan twelve months upfront and pay the annual rate. Startup at RM 58 a month and Growth at RM 118 a month, with the discount on the invoice.': 'Pay your web design plan twelve months upfront and pay the annual rate. Startup at RM 58 a month and Growth at RM 118 a month, with the discount on the invoice.',
    'Before you decide': 'Before you decide',
    'Frequently asked questions': 'Frequently asked questions',
    'What you get': 'What you get',
    'To start the build': 'To start the build',
    'The week it takes': 'The week it takes',
    /* Plan table and service plan copy */
    'Growth plan': 'Growth plan',
    'Startup plan': 'Startup plan',
    'Business email': 'Business email',
    'Hosting, domain, SSL': 'Hosting, domain, SSL',
    'MySQL database': 'MySQL database',
    'Pages': 'Pages',
    'Revisions': 'Revisions',
    'Backups': 'Backups',
    'Google Analytics 4': 'Google Analytics 4',
    'CDN and anti-DDoS': 'CDN and anti-DDoS',
    'Technical support': 'Technical support',
    'Source code repository': 'Source code repository',
    'Monthly report': 'Monthly report',
    'per month': 'per month',
    'per month, billed monthly': 'per month, billed monthly',
    'per month, billed annually': 'per month, billed annually',
    'SEO': 'SEO',
    'For new businesses': 'For new businesses',
    'For growing businesses': 'For growing businesses',
    'For custom builds': 'For custom builds',
    'Great for growing businesses who want a professional image and improve their branding.': 'Great for growing businesses who want a professional image and improve their branding.',
    'Best for startup businesses and small companies that need to showcase their ideas.': 'Best for startup businesses and small companies that need to showcase their ideas.',
    'Best suited for businesses who want more customisations to fit their exact requirements.': 'Best suited for businesses who want more customisations to fit their exact requirements.',
    'How many times we can still change the look and the wording.': 'How many times we can still change the look and the wording.',
    'The files that make the website, kept so you can take them later.': 'The files that make the website, kept so you can take them later.',
    'Where the website stores things like products, forms and member details.': 'Where the website stores things like products, forms and member details.',
    'We keep the site online, give it a web address, and add the lock in the browser.': 'We keep the site online, give it a web address, and add the lock in the browser.',
    'A report that shows how many people visit your website.': 'A report that shows how many people visit your website.',
    'A saved copy of the website we can restore if something goes wrong.': 'A saved copy of the website we can restore if something goes wrong.',
    'You can contact us if the website has a problem or you need help.': 'You can contact us if the website has a problem or you need help.',
    /* The @ is escaped: vue-i18n reads a bare @ as the start of a linked message
       (@:key) and refuses to compile the string, which is what printed three
       "Message compilation error" lines per page load. {'@'} renders as @. The
       key on the left stays literal, because keys are looked up, not parsed. */
    'Your own email using the website name, like hello@yourshop.com.': 'Your own email using the website name, like hello{\'@\'}yourshop.com.',
    'Helps pages open faster, and helps the site stay up if someone overloads it.': 'Helps pages open faster, and helps the site stay up if someone overloads it.',
    'Until first launch': 'Until first launch',
    'See the plans': 'See the plans',
    'Request a project proposal': 'Request a project proposal',
    'I do not have a logo. Do you design one?': 'I do not have a logo. Do you design one?',
    /* Promo brief checklist */
    'RM 118 Annual Pack Promo': 'RM 118 Annual Pack Promo',
    'RM 299 Landing Page Promo': 'RM 299 Landing Page Promo',
    'Six things and we start': 'Six things and we start',
    'What we need from you': 'What we **need** from you',
    'Your business details': 'Your business details',
    'Your website address': 'Your website address',
    'Your content': 'Your content',
    'A start date': 'A start date',
    'How buyers reach you': 'How buyers reach you',
    'How much you can save': 'How much you can save',
    'Tell us when you want to begin and we set the build window from there.': 'Tell us when you want to begin and we set the build window from there.',
    'The exact name and spelling, so the site, the email and the invoice all match.': 'The exact name and spelling, so the site, the email and the invoice all match.',
    'Words, images and a logo in any form. We lay them out and tighten the copy.': 'Words, images and a logo in any form. We lay them out and tighten the copy.',
    'Phone, WhatsApp, email and address, placed where visitors can tap them.': 'Phone, WhatsApp, email and address, placed where visitors can tap them.',
    /* Site chrome */
    'By': 'By',
    /* The portfolio case study's shared vocabulary. Every case page names the
       same handful of industries, the same places, the same four kinds of
       build and the same colours, so the words live here rather than once per
       case page in the portfolio dictionary, where the fifth copy is the one that
       would eventually say something else. Colour names are here for the same
       reason: the palette component prints them, and it is the same component the
       brand kit sheet shows. */
    'Colour palette': 'Colour palette',
    'Bakery machinery manufacturing': 'Bakery machinery manufacturing',
    'Art and collectibles': 'Art and collectibles',
    Education: 'Education',
    Printing: 'Printing',
    Travel: 'Travel',
    Software: 'Software',
    Retail: 'Retail',
    'Car rental': 'Car rental',
    'Waste trading & transport': 'Waste trading & transport',
    'Johor, Malaysia': 'Johor, Malaysia',
    'Kuala Lumpur, Malaysia': 'Kuala Lumpur, Malaysia',
    'Batu Pahat, Johor': 'Batu Pahat, Johor',
    'Pasir Gudang, Johor': 'Pasir Gudang, Johor',
    'Melaka, Malaysia': 'Melaka, Malaysia',
    Malaysia: 'Malaysia',
    'Custom website': 'Custom website',
    'Custom website with a shop': 'Custom website with a shop',
    'Custom portal': 'Custom portal',
    'Product website': 'Product website',
    Navy: 'Navy',
    'Deep blue': 'Deep blue',
    'Brand blue': 'Brand blue',
    'Sky blue': 'Sky blue',
    Teal: 'Teal',
    Lagoon: 'Lagoon',
    Jade: 'Jade',
    Forest: 'Forest',
    Amber: 'Amber',
    Gold: 'Gold',
    Orange: 'Orange',
    Crimson: 'Crimson',
    Magenta: 'Magenta',
    Violet: 'Violet',
    Plum: 'Plum',
    Rose: 'Rose',
    Ink: 'Ink',
    Paper: 'Paper',
    Cloud: 'Cloud',
    Mist: 'Mist',
    Stone: 'Stone',
    Sand: 'Sand',
    /* The accessible names for a screenshot inside a device frame. The service
       page's app stage and the portfolio's device band both put a capture on a
       laptop, a tablet and a phone, so the three sentences are shared vocabulary
       rather than one page's copy, and they are interpolated with the domain the
       page already prints. */
    '{domain} on a desktop': '{domain} on a desktop',
    '{domain} on a tablet': '{domain} on a tablet',
    '{domain} on a phone': '{domain} on a phone',
    /* The website assistant's empty state, and the reason it is here.

       Both chat surfaces open with these two lines: the floating widget on
       every page and the box on the AI page. They held their own copies under
       four different key names, and the second pair existed only so the two
       surfaces could say the same words twice. They are now one pair, so the
       two surfaces cannot greet the reader differently.

       The greeting is not a message. It is what the panel shows while the
       thread has no turns, so it is never written to the transcript, never
       counted and never sent to the model as something the assistant said. */
    '👋 Hi there! Welcome to {site}. I am here to help you find your way.': '👋 Hi there! Welcome to {site}. I am here to help you find your way.',
    'What would you like to know about?': 'What would you like to know about?',
    /* The enquiry form and both chat surfaces say this when the check does not
       return a token. One sentence, so the form and the chat cannot drift. */
    'The anti-bot check failed. Please try again.': 'The anti-bot check failed. Please try again.',
    'Verifying you are human...': 'Verifying you are human...',
    /* The message a WhatsApp link opens with. The footer, the enquiry band,
       the website report and the corner button all use it, in the language of
       the page, so the team can see which language to answer in. `{name}` is
       the partner's name when this visit still has a referral. */
    'Hi there, I would like to know more about your services.': 'Hi there, I would like to know more about your services.',
    'Hi there, I was referred by {name} and would like to know more about your services.': 'Hi there, I was referred by {name} and would like to know more about your services.',
},
  'zh-MY': {
    Home: '网站首页',
    Blog: '博客文章',
    Portfolios: '作品展示',
    Promotions: '限时优惠',
    Services: '服务项目',
    'Get in touch': '联系我们',
    /* 手机菜单按钮只有图标，所以这条是它的可访问名称。 */
    Menu: '菜单',
    'Web design': '网页设计',
    'Digital marketing': '数字营销',
    'Brand presence': '品牌形象',
    'Mobile application': '移动应用',
    'AI Solutions': 'AI 方案',
    Days: '天数',
    Hours: '小时',
    Minutes: '分钟',
    Seconds: '秒钟',
    Legal: '法律',
    'Privacy Policy': '隐私政策',
    'Terms of Use': '使用条款',
    'Most Popular': '最受欢迎',
    Price: '价格',
    Saves: '可省',
    'Paying less does not mean getting less.': '少付钱，不代表少得到。',
    Business: '商务',
    Catalog: '目录',
    'E-Commerce': '电商',
    Educational: '教育',
    Informational: '咨询',
    Portal: '门户',
    'Visit Website': '访问网站',
    'Pay your web design plan twelve months upfront and pay the annual rate. Startup at RM 58 a month and Growth at RM 118 a month, with the discount on the invoice.': '一次付清网页设计配套十二个月，即可享有年付价格。启动配套每月 RM 58，成长配套每月 RM 118，折扣会列在发票上。',
    'Before you decide': '做决定之前',
    'Frequently asked questions': '常见**问题**',
    'What you get': '您会得到',
    'To start the build': '开始制作',
    'The week it takes': '所需的一周',
    /* Plan table and service plan copy */
    'Growth plan': '成长配套',
    'Startup plan': '启动配套',
    'Business email': '商务邮箱',
    'Hosting, domain, SSL': '托管、域名、SSL',
    'MySQL database': 'MySQL 数据库',
    'Pages': '页面',
    'Revisions': '修改次数',
    'Backups': '备份',
    'Google Analytics 4': 'Google Analytics 4',
    'CDN and anti-DDoS': 'CDN 与 DDoS 防护',
    'Technical support': '技术支援',
    'Source code repository': '源代码仓库',
    'Monthly report': '每月报告',
    'per month': '每月',
    'per month, billed monthly': '每月，按月计费',
    'per month, billed annually': '每月，按年计费',
    'SEO': 'SEO',
    'For new businesses': '刚起步的生意',
    'For growing businesses': '正在扩充的生意',
    'For custom builds': '高度客制的项目',
    'Great for growing businesses who want a professional image and improve their branding.': '适合想树立专业形象、把品牌做起来的成长中企业。',
    'Best for startup businesses and small companies that need to showcase their ideas.': '适合刚创业、想把点子好好呈现出来的小公司。',
    'Best suited for businesses who want more customisations to fit their exact requirements.': '适合需要更多定制、完全贴合需求的公司。',
    'How many times we can still change the look and the wording.': '上线前还能改几次外观和文字。',
    'The files that make the website, kept so you can take them later.': '组成网站的文件，帮您留着，日后想带走随时可以。',
    'Where the website stores things like products, forms and member details.': '网站用来存放产品、表单和会员资料的地方。',
    'We keep the site online, give it a web address, and add the lock in the browser.': '我们让网站保持上线、给它网址，并加上浏览器的锁。',
    'A report that shows how many people visit your website.': '一份报告，让您看到有多少人来到您的网站。',
    'A saved copy of the website we can restore if something goes wrong.': '网站的存档副本，出事时我们可以还原。',
    'You can contact us if the website has a problem or you need help.': '网站有问题，或您需要帮忙，随时可以找我们。',
    'Your own email using the website name, like hello@yourshop.com.': '用自己网站名称的邮箱，例如 hello{\'@\'}yourshop.com。',
    'Helps pages open faster, and helps the site stay up if someone overloads it.': '让页面开得更快，也让网站在流量冲击时撑住。',
    'Until first launch': '到首次上线为止',
    'See the plans': '查看配套',
    'Request a project proposal': '索取项目提案',
    'I do not have a logo. Do you design one?': '我没有标志，你们会设计吗？',
    /* Promo brief checklist */
    'RM 118 Annual Pack Promo': 'RM 118 年付配套优惠',
    'RM 299 Landing Page Promo': 'RM 299 落地页优惠',
    'Six things and we start': '六项资料，随即开工',
    'What we need from you': '我们**需要**您提供',
    'Your business details': '您的营业资料',
    'Your website address': '您的网站网址',
    'Your content': '您的内容',
    'A start date': '开始日期',
    'How buyers reach you': '客户如何联系您',
    'How much you can save': '您能省下多少',
    'Tell us when you want to begin and we set the build window from there.': '告诉我们您想何时开始，我们据此安排制作时段。',
    'The exact name and spelling, so the site, the email and the invoice all match.': '正确的名称与写法，让网站、邮箱与发票保持一致。',
    'Words, images and a logo in any form. We lay them out and tighten the copy.': '文字、图片与标志，任何格式都可以。我们负责排版并精简文案。',
    'Phone, WhatsApp, email and address, placed where visitors can tap them.': '电话、WhatsApp、邮箱与地址，放在访客随手可点的位置。',
    /* Site chrome */
    'By': '作者',
    /* 作品案例页的共用词汇。每一个案例页都会重复用到同样的行业、同样的地点、
       同样的四种专案类型与同样的颜色，所以这些词放在这里，而不是在作品集词典里
       逐页各写一次。颜色名称放这里也是同一个理由：它们由色板组件印出，而品牌套件
       页用的是同一个组件。 */
    'Colour palette': '配色方案',
    'Bakery machinery manufacturing': '烘焙机械制造',
    'Art and collectibles': '艺术与收藏品',
    Education: '教育',
    Printing: '印刷',
    Travel: '旅游',
    Software: '软件',
    Retail: '零售',
    'Car rental': '汽车租赁',
    'Waste trading & transport': '废料收购与运输',
    'Johor, Malaysia': '马来西亚柔佛',
    'Kuala Lumpur, Malaysia': '马来西亚吉隆坡',
    'Batu Pahat, Johor': '柔佛峇株巴辖',
    'Pasir Gudang, Johor': '柔佛巴西古当',
    'Melaka, Malaysia': '马来西亚马六甲',
    Malaysia: '马来西亚',
    'Custom website': '客制网站',
    'Custom website with a shop': '带商店的客制网站',
    'Custom portal': '客制门户',
    'Product website': '产品网站',
    Navy: '藏青',
    'Deep blue': '深蓝',
    'Brand blue': '品牌蓝',
    'Sky blue': '天蓝',
    Teal: '蓝绿',
    Lagoon: '潟湖蓝',
    Jade: '玉绿',
    Forest: '森林绿',
    Amber: '琥珀',
    Gold: '金',
    Orange: '橙',
    Crimson: '绯红',
    Magenta: '洋红',
    Violet: '紫罗兰',
    Plum: '梅紫',
    Rose: '玫瑰',
    Ink: '墨黑',
    Paper: '纸白',
    Cloud: '云白',
    Mist: '雾紫',
    Stone: '石灰',
    Sand: '沙色',
    /* The accessible names for a screenshot inside a device frame. */
    '{domain} on a desktop': '{domain} 桌面版',
    '{domain} on a tablet': '{domain} 平板版',
    '{domain} on a phone': '{domain} 手机版',
    /* 网站助理的空状态，与上方英文段落同一组键。 */
    '👋 Hi there! Welcome to {site}. I am here to help you find your way.': '👋 你好！欢迎来到 {site}。我在这里为您指路。',
    'What would you like to know about?': '您想了解什么？',
    'The anti-bot check failed. Please try again.': '人机验证未通过，请再试一次。',
    'Verifying you are human...': '正在验证您是否为真人...',
    /* WhatsApp 开场白，与上方英文同一组键。语言跟着页面走。 */
    'Hi there, I would like to know more about your services.': '你好，我想进一步了解你们的服务。',
    'Hi there, I was referred by {name} and would like to know more about your services.': '你好，我是由 {name} 推荐的，想进一步了解你们的服务。',
},
  'ms-MY': {
    Home: 'Laman Utama',
    Blog: 'Blog',
    Portfolios: 'Portfolio',
    Promotions: 'Promosi',
    Services: 'Perkhidmatan',
    'Get in touch': 'Hubungi Kami',
    /* Butang menu mudah alih hanya memaparkan ikonnya, jadi ini nama
       kebolehcapaiannya. */
    Menu: 'Menu',
    'Web design': 'Reka Bentuk Web',
    'Digital marketing': 'Pemasaran Digital',
    'Brand presence': 'Kehadiran Jenama',
    'Mobile application': 'Aplikasi Mudah Alih',
    'AI Solutions': 'Penyelesaian AI',
    Days: 'Hari',
    Hours: 'Jam',
    Minutes: 'Minit',
    Seconds: 'Saat',
    Legal: 'Perundangan',
    'Privacy Policy': 'Dasar Privasi',
    'Terms of Use': 'Terma Penggunaan',
    'Most Popular': 'Paling Popular',
    Price: 'Harga',
    Saves: 'Jimat',
    'Paying less does not mean getting less.': 'Bayar kurang bukan bermaksud dapat kurang.',
    Business: 'Perniagaan',
    Catalog: 'Katalog',
    'E-Commerce': 'E-dagang',
    Educational: 'Pendidikan',
    Informational: 'Maklumat',
    Portal: 'Portal',
    'Visit Website': 'Melawat laman web',
    'Pay your web design plan twelve months upfront and pay the annual rate. Startup at RM 58 a month and Growth at RM 118 a month, with the discount on the invoice.': 'Bayar pelan reka bentuk web anda dua belas bulan di hadapan dan nikmati kadar tahunan. Startup RM 58 sebulan dan Growth RM 118 sebulan, dengan diskaun pada invois.',
    'Before you decide': 'Sebelum anda putuskan',
    'Frequently asked questions': '**Soalan** lazim',
    'What you get': 'Apa yang anda dapat',
    'To start the build': 'Untuk mula membina',
    'The week it takes': 'Seminggu yang diambil',
    /* Plan table and service plan copy */
    'Growth plan': 'Pelan Growth',
    'Startup plan': 'Pelan Startup',
    'Business email': 'E-mel perniagaan',
    'Hosting, domain, SSL': 'Pengehosan, domain, SSL',
    'MySQL database': 'Pangkalan data MySQL',
    'Pages': 'Halaman',
    'Revisions': 'Semakan',
    'Backups': 'Sandaran',
    'Google Analytics 4': 'Google Analytics 4',
    'CDN and anti-DDoS': 'CDN dan perlindungan anti-DDoS',
    'Technical support': 'Sokongan teknikal',
    'Source code repository': 'Repositori kod sumber',
    'Monthly report': 'Laporan bulanan',
    'per month': 'sebulan',
    'per month, billed monthly': 'sebulan, dibilkan setiap bulan',
    'per month, billed annually': 'sebulan, dibilkan setiap tahun',
    'SEO': 'SEO',
    'For new businesses': 'Untuk perniagaan yang baru bermula',
    'For growing businesses': 'Untuk perniagaan yang sedang berkembang',
    'For custom builds': 'Untuk projek yang perlu disesuaikan',
    'Great for growing businesses who want a professional image and improve their branding.': 'Sesuai untuk perniagaan yang sedang berkembang dan mahu kelihatan lebih profesional serta membina jenama sendiri.',
    'Best for startup businesses and small companies that need to showcase their ideas.': 'Sesuai untuk syarikat yang baru bermula dan mahu idea mereka dipamerkan dengan kemas.',
    'Best suited for businesses who want more customisations to fit their exact requirements.': 'Sesuai untuk perniagaan yang ada keperluan khas dan mahu semuanya dibina ikut cara mereka sendiri.',
    'How many times we can still change the look and the wording.': 'Berapa kali lagi kami boleh ubah rupa dan ayat.',
    'The files that make the website, kept so you can take them later.': 'Fail yang membentuk laman web itu, kami simpan supaya anda boleh bawa kemudian.',
    'Where the website stores things like products, forms and member details.': 'Tempat laman web menyimpan perkara seperti produk, borang dan butiran ahli.',
    'We keep the site online, give it a web address, and add the lock in the browser.': 'Kami pastikan laman kekal dalam talian, berikannya alamat web, dan menambah kunci pada pelayar.',
    'A report that shows how many people visit your website.': 'Laporan yang menunjukkan berapa ramai orang melawat laman web anda.',
    'A saved copy of the website we can restore if something goes wrong.': 'Salinan laman web yang disimpan, boleh kami pulihkan jika berlaku masalah.',
    'You can contact us if the website has a problem or you need help.': 'Anda boleh hubungi kami bila-bila masa laman web ada masalah atau anda perlukan bantuan.',
    'Your own email using the website name, like hello@yourshop.com.': 'E-mel anda sendiri menggunakan nama laman web, seperti hello{\'@\'}yourshop.com.',
    'Helps pages open faster, and helps the site stay up if someone overloads it.': 'Membantu halaman dibuka lebih pantas, dan membantu laman kekal dalam talian jika trafik melonjak.',
    'Until first launch': 'Sehingga pelancaran pertama',
    'See the plans': 'Lihat pelan',
    'Request a project proposal': 'Minta cadangan projek',
    'I do not have a logo. Do you design one?': 'Saya tiada logo. Adakah anda merekanya?',
    /* Promo brief checklist */
    'RM 118 Annual Pack Promo': 'Promosi Pek Tahunan RM 118',
    'RM 299 Landing Page Promo': 'Promosi Halaman Pendaratan RM 299',
    'Six things and we start': 'Enam perkara, kemudian kami mula',
    'What we need from you': 'Apa yang kami **perlukan** daripada anda',
    'Your business details': 'Butiran perniagaan anda',
    'Your website address': 'Alamat laman web anda',
    'Your content': 'Kandungan anda',
    'A start date': 'Tarikh mula',
    'How buyers reach you': 'Cara pembeli hubungi anda',
    'How much you can save': 'Berapa banyak anda boleh jimat',
    'Tell us when you want to begin and we set the build window from there.': 'Beritahu kami bila anda mahu mula dan kami tetapkan jadual bina dari situ.',
    'The exact name and spelling, so the site, the email and the invoice all match.': 'Nama dan ejaan yang tepat, supaya laman, e-mel dan invois semuanya sepadan.',
    'Words, images and a logo in any form. We lay them out and tighten the copy.': 'Teks, imej dan logo dalam apa jua bentuk. Kami susun dan kemaskan salinannya.',
    'Phone, WhatsApp, email and address, placed where visitors can tap them.': 'Telefon, WhatsApp, e-mel dan alamat, diletak di tempat pelawat boleh tekan.',
    /* Site chrome */
    'By': 'Oleh',
    /* Perbendaharaan kata kajian kes portfolio. Setiap halaman kajian kes
       menamakan industri yang sama, tempat yang sama, empat jenis binaan
       yang sama dan warna yang sama, jadi perkataan itu tinggal di sini dan bukan
       sekali pada setiap halaman di dalam kamus portfolio. Nama warna ada di sini atas sebab
       yang sama: ia dicetak oleh komponen palet, dan lembaran kit jenama
       menggunakan komponen yang sama. */
    'Colour palette': 'Palet warna',
    'Bakery machinery manufacturing': 'Pembuatan mesin bakeri',
    'Art and collectibles': 'Seni dan barangan koleksi',
    Education: 'Pendidikan',
    Printing: 'Percetakan',
    Travel: 'Pelancongan',
    Software: 'Perisian',
    Retail: 'Peruncitan',
    'Car rental': 'Sewaan kereta',
    'Waste trading & transport': 'Dagangan bahan buangan & pengangkutan',
    'Johor, Malaysia': 'Johor, Malaysia',
    'Kuala Lumpur, Malaysia': 'Kuala Lumpur, Malaysia',
    'Batu Pahat, Johor': 'Batu Pahat, Johor',
    'Pasir Gudang, Johor': 'Pasir Gudang, Johor',
    'Melaka, Malaysia': 'Melaka, Malaysia',
    Malaysia: 'Malaysia',
    'Custom website': 'Laman web tersuai',
    'Custom website with a shop': 'Laman web tersuai dengan kedai',
    'Custom portal': 'Portal tersuai',
    'Product website': 'Laman web produk',
    Navy: 'Biru navy',
    'Deep blue': 'Biru tua',
    'Brand blue': 'Biru jenama',
    'Sky blue': 'Biru langit',
    Teal: 'Biru hijau',
    Lagoon: 'Biru lagun',
    Jade: 'Hijau jed',
    Forest: 'Hijau hutan',
    Amber: 'Ambar',
    Gold: 'Emas',
    Orange: 'Oren',
    Crimson: 'Merah pekat',
    Magenta: 'Magenta',
    Violet: 'Ungu',
    Plum: 'Ungu plum',
    Rose: 'Ros',
    Ink: 'Hitam dakwat',
    Paper: 'Putih kertas',
    Cloud: 'Putih awan',
    Mist: 'Ungu kabus',
    Stone: 'Kelabu batu',
    Sand: 'Warna pasir',
    /* The accessible names for a screenshot inside a device frame. */
    '{domain} on a desktop': '{domain} pada desktop',
    '{domain} on a tablet': '{domain} pada tablet',
    '{domain} on a phone': '{domain} pada telefon',
    /* Keadaan kosong pembantu laman web, kumpulan kunci yang sama seperti di atas. */
    '👋 Hi there! Welcome to {site}. I am here to help you find your way.': '👋 Hai! Selamat datang ke {site}. Saya di sini untuk membantu anda.',
    'What would you like to know about?': 'Apa yang anda ingin tahu?',
    'The anti-bot check failed. Please try again.': 'Semakan anti-bot gagal. Sila cuba lagi.',
    'Verifying you are human...': 'Sedang mengesahkan bahawa anda adalah manusia...',
    /* Ucapan WhatsApp, kumpulan kunci yang sama seperti di atas. Bahasanya
       mengikut halaman. */
    'Hi there, I would like to know more about your services.': 'Hai, saya ingin mengetahui lebih lanjut tentang perkhidmatan anda.',
    'Hi there, I was referred by {name} and would like to know more about your services.': 'Hai, saya dirujuk oleh {name} dan ingin mengetahui lebih lanjut tentang perkhidmatan anda.',
}
};
